import React, { useEffect, useState } from 'react';
import { useKanban } from '../context/KanbanContext';

interface MetricasPanelProps {
    onClose: () => void;
}

export const MetricasPanel: React.FC<MetricasPanelProps> = ({ onClose }) => {
    const { metricas, fetchMetricas } = useKanban();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchMetricas();
            setLoading(false);
        })();
    }, [fetchMetricas]);

    const formatDias = (valor: number | null): string => {
        if (valor === null) return '—';
        return `${valor} día${valor === 1 ? '' : 's'}`;
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-lg">
                            📊
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Métricas de Flujo</h2>
                            <p className="text-xs text-slate-500">Lead Time, Cycle Time y Throughput</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Calculando métricas...</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-xs text-slate-500 mb-1">⏱️ Lead Time promedio</p>
                                    <p className="text-2xl font-bold text-slate-800">{formatDias(metricas?.lead_time_promedio_dias ?? null)}</p>
                                    <p className="text-[11px] text-slate-400 mt-1">Desde que la tarea entra al Backlog hasta que llega a Producción.</p>
                                </div>
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-xs text-slate-500 mb-1">⚙️ Cycle Time promedio</p>
                                    <p className="text-2xl font-bold text-slate-800">{formatDias(metricas?.cycle_time_promedio_dias ?? null)}</p>
                                    <p className="text-[11px] text-slate-400 mt-1">Desde que el trabajo activo comienza (En Progreso) hasta Producción.</p>
                                </div>
                                <div className="bg-emerald-50 rounded-xl p-4">
                                    <p className="text-xs text-emerald-600 mb-1">📦 Throughput (7 días)</p>
                                    <p className="text-2xl font-bold text-emerald-700">{metricas?.throughput_7d ?? 0}</p>
                                    <p className="text-[11px] text-emerald-600/80 mt-1">Tareas completadas en los últimos 7 días.</p>
                                </div>
                                <div className="bg-emerald-50 rounded-xl p-4">
                                    <p className="text-xs text-emerald-600 mb-1">📦 Throughput (30 días)</p>
                                    <p className="text-2xl font-bold text-emerald-700">{metricas?.throughput_30d ?? 0}</p>
                                    <p className="text-[11px] text-emerald-600/80 mt-1">Tareas completadas en los últimos 30 días.</p>
                                </div>
                            </div>

                            <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                                <span className="text-sm text-slate-600">Total de tareas completadas (histórico)</span>
                                <span className="text-lg font-bold text-slate-800">{metricas?.total_completadas ?? 0}</span>
                            </div>

                            {(metricas?.total_completadas ?? 0) === 0 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                                    Aún no hay tareas marcadas como "Producción / Listo". Las métricas se calcularán automáticamente a medida que se completen tareas.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
